import _ from "lodash";
import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import i18next from "src/i18n/config";
import { PermissionService } from "src/services/permissionService/permissionService";

import { ConfirmDialog } from "@dashboard/devices/components/shared/index";
import BuildsTable from "@dashboard/products/components/Builds/BuildsTable";
import { useStyles } from "@dashboard/products/ProductPage.style";
import { Product } from "@dashboard/products/types/index";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import {
    useCurrentSubscriptionExpired,
    useCurrentWorkspace,
    useIsWorkspaceFeatureLimitReached,
} from "@dashboard/workspaces/api/helpers";
import { DialogContentText } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { DangerOutlinedButton, PrimaryOutlinedButton } from "@shared/CustomButton";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { SingleSelectFieldProps } from "@shared/form/SelectField/SelectField.types";
import { LoadingBox } from "@shared/Loading";
import { showSuccessToast } from "@shared/toasts/Toasts";

import { SidePanel } from "../shared/components/SidePanel/SidePanel";

import { useCurrentWorkspaceProducts } from "./api/helpers";
import { ProductBuilder } from "./components/ProductBuilder/ProductBuilder";
import { useDeleteProductMutation } from "./api";
import { useChangeProductFromUrl, useCurrentProduct } from "./ProductPage.utils";

export const ProductManagerPage = (props: { products: Product[] }) => {
    const [currentProductId, setCurrentProductId] = useCurrentProduct(props.products);
    const [isDeleteProductDialogOpen, setIsDeleteProductDialogOpen] = React.useState(false);

    const { classes } = useStyles();
    const navigate = useNavigate();

    const { t } = useTranslation(["pages", "common", "modals"]);

    const [deleteProduct] = useDeleteProductMutation();

    const { data: workspace } = useCurrentWorkspace();
    const isOverlimit = useIsWorkspaceFeatureLimitReached("ProductLimit");

    const changeProductFromUrl = useChangeProductFromUrl({ setCurrentProductId });

    const isExpired = useCurrentSubscriptionExpired();
    const { productAbility } = PermissionService();
    const cannotManageProducts = productAbility(workspace).cannot("manage", "Product");
    // Deleting stays allowed after expiry so the customer can clean up their own products.
    const cannotDeleteProducts = productAbility(workspace).cannot("delete", "Product");

    const currentProduct = props.products.find((item) => item.id === currentProductId);
    const isCreationProcess = !currentProduct?.id;
    const productOptions = props.products.map((product) => ({
        label: `${product.customBuildConfig?.productName || ""} - Android ${product.androidVersion}`,
        value: product.id,
    }));

    const updateProductUrl = (product: Product | undefined) => {
        if (product) {
            void navigate(`/dashboard/products/${product.codename}/${product.androidVersion}`);
        }
    };

    const onCreateProduct = () => {
        setCurrentProductId(null);
        void navigate("/dashboard/products");
    };

    const switchToProduct = async (product: Product) => {
        setCurrentProductId(product.id);
        updateProductUrl(product);
    };

    const handleProductChange: SingleSelectFieldProps["onChange"] = async (_event, selectedOption) => {
        if (!selectedOption || typeof selectedOption.value !== "number") {
            return;
        }

        const selectedProduct = props.products.find((product) => product.id === selectedOption.value);
        if (!selectedProduct) {
            return;
        }

        await switchToProduct(selectedProduct);
    };

    const onCancelCreation = async () => {
        const firstProduct = props.products[0];
        if (!firstProduct) {
            return;
        }
        await switchToProduct(firstProduct);
    };

    const getCreateButtonTooltip = () => {
        if (isOverlimit) {
            return t("pages:productPage.tooltip.maxProductLimitReached");
        }
        if (isExpired) {
            return t("pages:productPage.tooltip.subscriptionExpired");
        }
        if (cannotManageProducts) {
            return t("pages:productPage.tooltip.noPermissionToCreateProduct");
        }
        return "";
    };

    const confirmDeleteProductDialog = async () => {
        if (currentProductId) {
            const result = await deleteProduct(currentProductId);
            if (!("error" in result)) {
                showSuccessToast(t("pages:productPage.toast.successfullyDeletedProduct"));
                const filterProducts = props.products.filter((item) => item.id !== currentProductId);
                setCurrentProductId(filterProducts[0]?.id);
            }

            setIsDeleteProductDialogOpen(false);
        }
    };

    React.useEffect(() => {
        void changeProductFromUrl();
    }, []);

    return (
        <React.Fragment>
            <PageContainer>
                <SelectField
                    label={isCreationProcess ? t("pages:productPage.input.createNewProduct.caption") : t("common:label.product")}
                    value={productOptions.find((option) => option.value === currentProductId)}
                    onChange={handleProductChange}
                    options={productOptions}
                    disabled={!props.products.length}
                    fullWidth
                    searchable
                />

                <div className={classes.buttonsContainer}>
                    <DangerOutlinedButton
                        onClick={() => setIsDeleteProductDialogOpen(true)}
                        className={classes.deleteButton}
                        disabled={!props.products.length || cannotDeleteProducts || isCreationProcess}
                        tooltipProps={{
                            title: t("pages:productPage.tooltip.noPermissionToDeleteProduct"),
                            hide: !cannotDeleteProducts,
                        }}
                    >
                        {t("common:button.delete")}
                    </DangerOutlinedButton>
                    <PrimaryOutlinedButton
                        onClick={onCreateProduct}
                        disabled={isOverlimit || cannotManageProducts || isCreationProcess}
                        tooltipProps={{
                            title: getCreateButtonTooltip(),
                        }}
                    >
                        {t("common:button.create")}
                    </PrimaryOutlinedButton>
                </div>

                {isCreationProcess && isOverlimit ? (
                    <CustomAlert severity="warning" className={classes.alertContainer}>
                        {getCreateButtonTooltip()}
                    </CustomAlert>
                ) : null}
                <React.Fragment>
                    <ProductBuilder
                        product={currentProduct}
                        setCurrentProductId={setCurrentProductId}
                        onCancelCreation={onCancelCreation}
                    />
                    {currentProduct ? <BuildsTable product={currentProduct} /> : null}
                </React.Fragment>
            </PageContainer>
            <ConfirmDialog
                title={t("modals:deleteProduct.title")}
                open={isDeleteProductDialogOpen}
                onConfirm={confirmDeleteProductDialog}
                onClose={() => setIsDeleteProductDialogOpen(false)}
                dangerButton
                primaryActionText={t("common:button.delete")}
            >
                <DialogContentText>
                    <Trans
                        ns="modals"
                        i18nKey="deleteProduct.description"
                        values={{ productName: currentProduct?.customBuildConfig?.productName || "" }}
                        components={{ bold: <span className={classes.boldText} /> }}
                    />
                </DialogContentText>
            </ConfirmDialog>
            <SidePanel />
        </React.Fragment>
    );
};

const ProductPage = () => {
    const { data: products = [], isLoading } = useCurrentWorkspaceProducts();

    const sortedProducts = _.sortBy([...products], (item) => [item.customBuildConfig?.productName?.toLowerCase()]);

    if (isLoading) {
        return <LoadingBox />;
    }

    return <ProductManagerPage products={sortedProducts} />;
};

export default withMetaDescription(ProductPage, {
    title: i18next.t("pages:productPage.metaTitle"),
    description: i18next.t("pages:productPage.metaDescription"),
});
